<script module lang="ts">
	export interface FieldOption {
		name: string;
		value: string;
		field: Field;
	}
</script>

<script lang="ts">
	import { Dropdown, DropdownItem, Frame } from "flowbite-svelte";
	import { Badge } from "flowbite-svelte-v1";
	import { twMerge } from "tailwind-merge";
	import type { Field } from "#gen/forms/v1/field_pb.js";
	import type { Rule } from "#gen/forms/v1/forms_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import TextDivider from "#lib/components/TextDivider.svelte";
	import JsonRuleEditor from "#lib/components/page/components/form/conditionals/JsonRuleEditor.svelte";
	import Button from "#lib/ui/Button/index.js";

	interface Props {
		fields: FieldOption[];
		rule: Rule;
		index: number;
		onRemove: (i: number) => void;
		onDuplicate: (i: number) => void;
		onChange: (i: number, rule: Rule) => void;
	}

	let { fields, index, rule = $bindable(), onRemove, onDuplicate, onChange }: Props = $props();
	let usedFields: string[] = $state([]);
	let schema = $derived(rule.schema);
	let search = $state("");
	let selection = $state(0);
	let uniquelyNamedFields = $derived(makeUniqueNames(fields));
	let availableFields = $derived(uniquelyNamedFields.filter(f => !usedFields.includes(f.value)));
	let selectedFields = $derived(uniquelyNamedFields.filter(f => rule.fieldKeys.includes(f.value)));
	let filteredFields = $derived(availableFields.filter(f => !rule.fieldKeys.includes(f.value)).filter(field => field.name.toLowerCase().includes(search.toLowerCase())));
	let dropdownOpen = $state(false);

	function clearSearch() {
		setTimeout(() => {
			search = "";
		}, 100);
	}

	function openDropdown() {
		dropdownOpen = true;
	}

	function closeDropdown() {
		dropdownOpen = false;
		selection = 0;
	}

	function remove() {
		onRemove(index);
	}

	function duplicate() {
		onDuplicate(index);
	}

	function change() {
		onChange(index, rule);
	}

	function onSchemaChange(s: string) {
		rule.schema = s;
		change();
	}

	function addField(value: string) {
		if (rule.fieldKeys.includes(value)) {
			return;
		}
		rule.fieldKeys = [...rule.fieldKeys, value];
		search = "";
		selection = 0;
		change();
	}

	function removeField(value: string) {
		rule.fieldKeys = rule.fieldKeys.filter(v => v !== value);
		change();
	}

	function handleKeyDown(e: KeyboardEvent) {
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				if (selection < filteredFields.length - 1) {
					selection++;
				}
				break;

			case "ArrowUp":
				e.preventDefault();
				if (selection > 0) {
					selection--;
				}
				break;

			case "Enter":
				e.preventDefault();
				if (filteredFields[selection]) {
					addField(filteredFields[selection].value);
					closeDropdown();
				}
				break;

			case "Backspace":
				if (!search && rule.fieldKeys.length > 0) {
					e.preventDefault();
					removeField(rule.fieldKeys[rule.fieldKeys.length - 1]);
				}
				break;
			case "Escape":
				closeDropdown();
				break;
		}
	}

	function makeUniqueNames(fields: FieldOption[]) {
		const counts: Record<string, number> = {};

		return fields.map(field => {
			counts[field.name] = (counts[field.name] ?? 0) + 1;

			return counts[field.name] === 1 ? field : { ...field, name: `${field.name} (${counts[field.name]})` };
		});
	}
</script>

<div role="button" tabindex="-1" class={twMerge("field-editor flex items-center rounded-lg border bg-white px-4 py-6 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500")}>
	<div class="grow">
		<JsonRuleEditor class="mb-4" fields={uniquelyNamedFields.filter(f => !rule.fieldKeys.includes(f.value))} {schema} bind:usedFields onChange={onSchemaChange} />
		<div>
			<TextDivider text="THEN" />
			<P class="mb-2 text-sm font-medium" color="text-gray-900">Then show fields</P>
			<div class="relative">
				<Frame rounded border class="flex min-h-11 w-full items-center gap-2 border-gray-300 py-1 pr-2 pl-2.5">
					<div class="flex min-w-0 grow flex-wrap items-center gap-2">
						<div class="flex min-w-0 flex-1 flex-wrap items-center gap-2 overflow-y-auto">
							{#each selectedFields as field (field.value)}
								<Badge color="gray" dismissable onclose={() => removeField(field.value)}>
									{field.name}
								</Badge>
							{/each}
							<input bind:value={search} onblur={clearSearch} oninput={openDropdown} onkeydown={handleKeyDown} placeholder={rule.fieldKeys.length === 0 ? "Search fields..." : ""} class="min-w-20 grow border-0 bg-transparent py-0 pl-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none" />
						</div>
						{#if rule.fieldKeys.length > 0 || search}
							<Button
								color="none"
								on:click={e => {
									e.stopPropagation();
									rule.fieldKeys = [];
									search = "";
									closeDropdown();
									change();
								}}
								data-action-name="form-rule-clear-fields"
								class="ml-auto flex h-8 w-8 items-center justify-center p-0"
							>
								<Icon name="x-mark" class="h-4 w-4" />
							</Button>
						{/if}
						<Icon name={dropdownOpen ? "chevron-up" : "chevron-down"} class="h-4 w-4" />
					</div>
				</Frame>
				<Dropdown bind:open={dropdownOpen} containerClass="w-full border border-gray-300 z-50">
					<div class="max-h-72 overflow-y-auto py-1">
						{#each filteredFields as field, idx (field.value)}
							<DropdownItem class={twMerge("px-4 py-3", idx === selection && "bg-gray-100 dark:bg-gray-600")} onclick={() => addField(field.value)}>
								<div class="flex items-center justify-between gap-3">
									<P class="font-medium text-gray-900">
										{field.name}
									</P>
								</div>
							</DropdownItem>
						{/each}
						{#if filteredFields.length === 0}
							<P class="px-4 text-gray-500">No fields found</P>
						{/if}
					</div>
				</Dropdown>
			</div>
		</div>
	</div>
	<div class="field-buttons mx-1 w-8 flex-none">
		<Button on:click={duplicate} color="light" class="mb-1 border-0 p-2! focus:border-0" data-action-name="form-rule-duplicate">
			<Icon name="document-duplicate" />
		</Button>
		<Button on:click={remove} color="light" class="border-0 p-2! focus:border-0" data-action-name="form-rule-delete">
			<Icon name="trash" />
		</Button>
	</div>
</div>

<style>
	:global(.field-editor .field-buttons) {
		opacity: 0;
	}
	:global(.field-editor:hover .field-buttons) {
		opacity: 1;
	}
</style>
